import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Building2, Moon, Sun, SlidersHorizontal } from 'lucide-react';
import { useFilters } from '../context/FiltersContext';
import { useTheme } from '../context/ThemeContext';
import { triggerHaptic } from '../utils/haptics';
import { FiltersSheet } from './Filters/FiltersSheet';
import { useDuckDB } from '../context/LazyDuckDBContext';
import { isEnginePath } from '../constants/engineRoutes';

import { motion } from 'framer-motion';

const BottomNav: React.FC = () => {
    const { filters, setFilters } = useFilters();
    const { theme, toggle: toggleTheme } = useTheme();
    const [filtersOpen, setFiltersOpen] = React.useState(false);
    const { ready, isInitializing } = useDuckDB();
    const location = useLocation();

    const isDataRoute = isEnginePath(location.pathname);

    // Calculate active filters count for badge
    const activeCount = [
        filters.kommunId && filters.kommunId !== 'null',
        filters.huvudmanId && filters.huvudmanId !== '',
        filters.skolaId && filters.skolaId !== 'null',
        filters.kpiId,
        filters.compareScope !== 'municipality'
    ].filter(Boolean).length;

    // Engine routes mount LazyDuckDBProvider in App (see ConditionalDuckDBShell); use ready/init only.
    // Do not gate on routeHasDuckDB — root DuckDBUnavailableProvider keeps it false until inner provider wins.
    const isLoading = isDataRoute && (!ready || isInitializing);

    const handleDataClick = (e: React.MouseEvent) => {
        if (isLoading) {
            e.preventDefault();
            e.stopPropagation();
        } else {
            triggerHaptic('light');
        }
    };

    return (
        <>
            <nav className="fixed bottom-0 left-0 right-0 z-[2100] glass border-t border-t-white/20 sm:hidden pb-[env(safe-area-inset-bottom)]">
                <div className="flex items-center justify-around h-16 px-2">

                    {/* Kommun Tab */}
                    <NavLink
                        to="/kommun"
                        onClick={handleDataClick}
                        className={({ isActive }) => `
              flex-1 flex flex-col items-center justify-center w-full h-full gap-1 transition-all duration-300
              ${isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'}
              ${isLoading ? 'cursor-not-allowed opacity-70' : ''}
            `}
                    >
                        <motion.div
                            animate={isLoading ? {
                                scale: [1, 1.15, 1],
                                opacity: [0.5, 1, 0.5],
                                filter: ["brightness(1)", "brightness(1.5)", "brightness(1)"]
                            } : {}}
                            transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        >
                            <Building2 className="w-6 h-6" strokeWidth={2} />
                        </motion.div>
                        <span className="text-[10px] font-medium">Kommun</span>
                    </NavLink>

                    {/* Filter Button (Hide on Landing Page) */}
                    {isDataRoute && (
                        <button
                            onClick={(e) => {
                                if (isLoading) {
                                    e.preventDefault();
                                } else {
                                    triggerHaptic('medium');
                                    setFiltersOpen(true);
                                }
                            }}
                            className={`flex-1 flex flex-col items-center justify-center w-full h-full gap-1 text-slate-500 dark:text-slate-400 relative group transition-all duration-300 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            aria-label="Öppna filter"
                            disabled={isLoading}
                        >
                            <div className={`
                p-3 rounded-2xl transition-all duration-300 relative
                ${activeCount > 0
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                    : 'bg-transparent text-slate-500 dark:text-slate-400 group-active:bg-slate-100 dark:group-active:bg-slate-800'
                                }
              `}>
                                <SlidersHorizontal className={`w-6 h-6 ${isLoading ? 'animate-spin' : ''}`} strokeWidth={2} />
                                {activeCount > 0 && !isLoading && (
                                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white ring-2 ring-white dark:ring-slate-900">
                                        {activeCount}
                                    </span>
                                )}
                            </div>
                        </button>
                    )}

                    {/* Theme Toggle - Always enabled */}
                    <button
                        onClick={() => {
                            triggerHaptic('light');
                            toggleTheme();
                        }}
                        className="flex-1 flex flex-col items-center justify-center w-full h-full gap-1 text-slate-500 dark:text-slate-400 active:scale-95 transition-transform"
                        aria-label="Växla tema"
                    >
                        {theme === 'dark' ? (
                            <Moon className="w-6 h-6" strokeWidth={2} />
                        ) : (
                            <Sun className="w-6 h-6" strokeWidth={2} />
                        )}
                        <span className="text-[10px] font-medium">Tema</span>
                    </button>

                </div>
            </nav>

            {/* Re-use FiltersSheet here so it works from bottom nav too (only when not on landing page) */}
            {isDataRoute && (
                <FiltersSheet
                    isOpen={filtersOpen}
                    onClose={() => setFiltersOpen(false)}
                    value={filters}
                    onChange={setFilters}
                />
            )}
        </>
    );
};

export default BottomNav;
